import { ReactNode } from 'react';
import { P, B, SubHead, NoteBox, TextLink } from 'app/components/StaticPages';
import { PHONE_NUMBER, CONTACT_EMAIL } from 'app/core/constants';
import { COLOR_POLICY_FAQ } from 'app/constants/colorDisclaimer';

export interface FaqItem {
  q: string;
  a: ReactNode;
}

export interface FaqGroup {
  label: string;
  items: FaqItem[];
}

export const FAQ_GROUPS: FaqGroup[] = [
  {
    label: 'Rentals & Delivery',
    items: [
      {
        q: 'How do I get my stroller rental?',
        a: (
          <>
            <SubHead sx={{ color: 'staticPages.orange' }}>
              New Option — Orlando International Airport
            </SubHead>
            <P sx={{ mb: 2 }}>
              Guests can pick up and return their stroller rental at the Orlando
              International Airport. The pick-up and return counter is located
              in Terminal A, Level 1 at the Wrap &apos;N Fly service counter
              (next to the USO lounge). The counter is staffed 7am–7pm, 7 days a
              week (pick-ups and returns before or after hours will be left in
              front of the service counter).
            </P>
            <SubHead>For those staying at a Disney resort</SubHead>
            <P sx={{ fontWeight: 600, color: 'staticPages.text.body' }}>
              Disney requires their resort guests to meet with our delivery
              driver for stroller or scooter rentals.
            </P>
            <P>
              All deliveries and pick-ups need to be made face to face with a
              representative of the company. Kingdom Strollers will meet you at
              a designated time and place for your delivery and return.
              It&apos;s extremely important that during the reservation process
              you choose a time when you&apos;ll be able to meet us for both
              delivery and the return pick-up. You&apos;ll receive delivery and
              return location information via email after your order is placed.
            </P>
            <P>
              Cribs, toddler beds, and bed rails are delivered directly to the
              luggage desk at Disney resorts, where they&apos;re held for your
              convenience. You do not need to meet a driver for these items.
            </P>
            <NoteBox>
              <B navy>This does not apply to:</B> Disney Springs hotels and
              resorts, Wyndham Bonnet Creek, Wyndham Grand at Bonnet Creek,
              Hilton Bonnet Creek, Waldorf Astoria Orlando, The Swan &amp; Swan
              Reserve Resort at Walt Disney World, The Dolphin Resort at Walt
              Disney World, and Four Seasons Resort Orlando at Walt Disney
              World.
            </NoteBox>
            <SubHead>For any other hotel, resort, or vacation home</SubHead>
            <P>
              Your stroller will be delivered to the bell services desk of the
              resort or hotel you list on the order form. Once you&apos;ve
              checked in, simply go to the bell services desk and claim it (they
              usually keep them in a luggage storage room). Be sure to give them
              the name you chose to list on your stroller if different than your
              last name. <B>You do not need to be present</B> for delivery to
              your resort or hotel. Please don&apos;t store your stroller
              overnight with the bell stands or luggage rooms — they have
              limited space and staff. Once you&apos;ve taken possession of your
              stroller, please keep it with you.
            </P>
            <P>
              If the stroller is being delivered to a private residence or
              vacation home, please indicate a delivery time <em>after</em>{' '}
              you&apos;ve arrived. For special delivery-time requests, call or
              email us so we can work out the details. We&apos;ll accommodate
              any request to the best of our ability.
            </P>
          </>
        ),
      },
      {
        q: 'What are the hours for the airport option?',
        a: <P>The airport counter is open 24 hours a day, 7 days a week.</P>,
      },
      {
        q: 'If I choose the airport option and our flight is delayed, what happens?',
        a: (
          <P>
            No worries! If you arrive after your chosen time, our area is a
            kiosk — just look for your name on the stroller and take it. We
            normally have someone there from 7am–3pm, but it&apos;s a
            self-service kiosk.
          </P>
        ),
      },
      {
        q: 'What if I forget to pick up my stroller from the airport?',
        a: (
          <P>
            We do our best to send several reminders for your rental pick-up. If
            you forget to pick up your stroller from the airport, it may be
            possible for us to deliver it to your hotel, resort, or vacation
            home the next morning. Due to the cost of delivering to the airport,
            there would be a $15 re-delivery fee for this service.
          </P>
        ),
      },
      {
        q: 'Where do you deliver?',
        a: (
          <>
            <P>
              We deliver to MOST Orlando attraction-area hotels, resorts, and
              vacation homes for FREE. The list of hotels and resorts we service
              is listed on the reservation page in a drop-down menu. For
              vacation home rentals, a good rule of thumb is 8–10 miles around
              Disney — Lake Buena Vista, North Kissimmee, Davenport, and
              Clermont are all within our delivery area. If you&apos;re not sure
              you&apos;re in that area, just ask us. We do not deliver to
              Downtown Orlando, east of the Florida Turnpike, airport-area
              hotels, Haines City, or St. Cloud. If your stay is outside our
              delivery area, delivery MAY still be possible for a $25 delivery
              fee (please call before placing an order if your requested
              delivery location is greater than 10 miles from Disney).
            </P>
            <P>
              <B navy>Locations we do NOT serve:</B> Marriott Grande Vista.
            </P>
          </>
        ),
      },
      {
        q: 'What if my drop-off and pick-up locations are not the same?',
        a: (
          <P>
            When ordering, you&apos;ll be given the option of where you&apos;d
            like delivery and drop-off to take place. Most likely these will be
            at a resort or hotel. If pick-up is at a private residence, please
            indicate a time that you&apos;ll be present at the residence.
          </P>
        ),
      },
      {
        q: 'Can I leave the stroller with the bell stand at night?',
        a: (
          <P>
            We ask that you maintain possession of the stroller when not in use.
            The hotels and resorts are kind enough to allow us to deliver and
            return rental items to their bell stands. Please do not store your
            stroller with the bell stand — it should remain in a secure location
            (hotel room or locked vehicle). Strollers found at the bell stands
            may be considered abandoned or returned early, and insurance does
            not cover rental items left overnight for daily storage with bell
            stands or luggage desks.
          </P>
        ),
      },
    ],
  },
  {
    label: 'Cleaning & Care',
    items: [
      {
        q: 'How clean are your strollers?',
        a: (
          <>
            <P sx={{ mb: 2 }}>
              Kingdom Strollers&apos; commitment to a clean, comfortable
              stroller rental has never changed. We take great pride in the
              cleanliness of our fleet.
            </P>
            <P sx={{ mb: 2 }}>
              Between each rental, City Mini, Mini GT, Summit, and Liberty
              strollers have their seat fabric completely removed and laundered
              using Ecolab commercial laundry detergents. Canopies are
              hand-cleaned using a hydrogen-peroxide-based cleaner and passed
              over with a steamer. Frames, wheels, and baskets are cleaned and
              wiped with disinfectant. Parent consoles, cooler bags, and rain
              covers are laundered between each rental. Child trays are
              hand-washed and cleaned with disinfectant. Stroller hooks are
              soaked in disinfectant, rinsed, and air dried.
            </P>
            <P>
              Between each rental, BOB stroller fabric is hand-cleaned with a
              hydrogen-peroxide-based cleaner, scrubbed, then injected with 200°
              water which is extracted (think upholstery cleaning). Canopies are
              hand-cleaned using a hydrogen-peroxide-based cleaner and passed
              over with a steamer. Frames, wheels, and baskets are cleaned and
              wiped with disinfectant. Parent consoles, cooler bags, and rain
              covers are laundered between each rental. Child trays are
              hand-washed and cleaned with disinfectant. Stroller hooks are
              soaked in disinfectant, rinsed, and air dried.
            </P>
          </>
        ),
      },
      {
        q: 'Are your strollers clean? (quick version)',
        a: (
          <P>
            Our City Mini, Summit X3, Mini GT, and Axiom models have their
            fabric removed and laundered using Ecolab commercial laundry
            detergent after each rental. Our BOB strollers are hand-cleaned with
            hot water, hydrogen-peroxide cleanser, and steam.
          </P>
        ),
      },
    ],
  },
  {
    label: 'Scooters & ECVs',
    items: [
      {
        q: 'How does my scooter turn on?',
        a: (
          <P>
            We&apos;ve transitioned to keyless scooters with a push-start
            option. We noticed that some guests were losing or breaking keys,
            which was a significant inconvenience. This new system enhances your
            experience and eliminates any key-related issues.
          </P>
        ),
      },
      {
        q: 'How long will my scooter last on a fully charged battery?',
        a: (
          <>
            <P sx={{ mb: 2 }}>
              We at Kingdom Strollers are the only ones that use a Lithium
              Extended battery scooter with an extended 16 MAXX amp-hour
              version, which gives you up to 14 miles per charge.{' '}
              <B>
                However, battery life will vary depending on the rider&apos;s
                weight and the park&apos;s terrain, such as hills and roads.
              </B>{' '}
              It&apos;s advised <B>not</B> to do Animal Kingdom &amp; Epcot on
              the same day — Animal Kingdom has inclines that are tougher on the
              battery, and Epcot is enormous (you can fit almost every theme
              park in Orlando into Epcot&apos;s acreage).
            </P>
            <P>
              We recommend turning it off when not in use to save as much
              battery as possible. We also provide scooter chargers that should
              always stay with the scooters (treat it like a cellphone charger
              cable) so you can charge it while parked at a restaurant, etc.
            </P>
          </>
        ),
      },
      {
        q: 'What happens if I have a scooter issue?',
        a: (
          <P>
            We want to ensure you have the best experience possible. Call our
            office immediately at <B navy>{PHONE_NUMBER}</B> and we&apos;ll help
            solve the issue. Only our managers handle calls, so you speak
            directly with someone knowledgeable and ready to assist — no
            automated voices involved. We value your time and aim to provide a
            personal touch in our communication.
          </P>
        ),
      },
      {
        q: 'Can my child or grandchild sit on the scooter with me while riding?',
        a: (
          <P>
            No. It throws off the center of gravity and weight distribution of
            the scooter and will quickly drain the battery. It also exceeds the
            weight limit and becomes a hazard as well as a liability.
          </P>
        ),
      },
      {
        q: 'Are the scooters waterproof?',
        a: (
          <P>
            As of 2025, there are zero waterproof scooters / ECVs made by any
            manufacturer. Light rain is typically not an issue, but puddles can
            and will ruin the scooter. Try to avoid rain at all costs —
            it&apos;s an electric scooter and should be parked or covered to
            avoid getting wet. All of our scooters come with a rain cover to
            help prevent water damage.
          </P>
        ),
      },
    ],
  },
  {
    label: 'Our Strollers',
    items: [
      {
        q: 'Why would I rent a stroller?',
        a: (
          <P>
            There are some very good reasons to rent a stroller, even if your
            children don&apos;t normally use one at home. Walking around a theme
            park all day can be VERY TIRING — as an adult, you&apos;ll feel it,
            so imagine little legs trying to keep up all day. Giving them a ride
            around the park will not only save them from getting cranky, but
            will save you from some of that mid-day frustration. These parks are
            big — I mean big — and you can easily walk a couple of miles a day
            just jumping from one side of the park to the other.
          </P>
        ),
      },
      {
        q: "Can't I just rent one from the parks?",
        a: (
          <>
            <P>
              All theme parks rent hard plastic strollers. Unfortunately, their
              strollers are not padded, don&apos;t recline, and cannot be
              removed from the parks.
            </P>
            <P>
              All strollers from Kingdom Strollers have padded seats, fully
              recline, and can be taken anywhere you want — and trust us, the
              last thing you want is to carry a sleeping child back to the car
              at 11:30 at night with 30,000 other people making a beeline for
              the same monorail you are.
            </P>
          </>
        ),
      },
      {
        q: 'Do you have a stroller for kids with special needs?',
        a: (
          <P>
            Yes, we offer the Axiom Special Needs Stroller. This stroller is
            good for children up to 5 feet tall and/or 100 lbs.
          </P>
        ),
      },
      { q: COLOR_POLICY_FAQ.q, a: <P>{COLOR_POLICY_FAQ.a}</P> },
    ],
  },
  {
    label: 'Stroller Tags & Labeling',
    items: [
      {
        q: 'How are the strollers labeled? How do I know which stroller is mine?',
        a: (
          <>
            <P>
              Every stroller is labeled with two different tags. A small
              handlebar tag contains the name of the person who placed the order
              (the renter), the dates of the rental, and a barcode used for
              delivery and pick-up tracking. This tag is required by the resorts
              to have the renter&apos;s name on it so the resort can verify that
              the guest is staying there if needed. A larger placard-style tag
              is attached to the axle or rear frame of the stroller — this is
              the tag you&apos;ll use to identify the stroller in the parks.
              It&apos;s normally just the guest&apos;s last name, but can be any
              name you&apos;d like it to read.
            </P>
            <P>
              For example, if your last name is Smith and you&apos;re concerned
              there are too many Smiths, or you&apos;re uncomfortable having
              your last name so visible, you could have your stroller tag say
              the &quot;Doozeldorf&quot; family. There is a 15-character limit
              in the space provided for the larger tag.
            </P>
            <P>
              Whatever name you choose, just remember to give this information
              to the bellman when picking up your stroller, as they may see the
              larger tag over the smaller one.
            </P>
          </>
        ),
      },
    ],
  },
  {
    label: 'Cribs & Bedding',
    items: [
      {
        q: 'How big are the cribs?',
        a: (
          <P>
            These 100% wooden cribs are 54&quot; wide, 30&quot; deep, and
            36&quot; tall when fully opened. Closed, they are a mere 10&quot;
            wide, so moving them into your room is pretty simple.
          </P>
        ),
      },
      {
        q: 'How hard are they to set up?',
        a: (
          <P>
            They&apos;ll literally take 20 seconds to set up. You&apos;ll be
            surprised at how easy it is.
          </P>
        ),
      },
      {
        q: 'Are they clean? Is the bedding clean?',
        a: (
          <P>
            We take great pride in the cleanliness of all of our products. The
            cribs are sanitized between each use and delivered covered to keep
            them as clean as possible. The bedding is washed with Ecolab
            commercial laundry products and placed into a plastic bag, which is
            delivered with your crib.
          </P>
        ),
      },
    ],
  },
  {
    label: 'Accessories',
    items: [
      {
        q: 'Do you offer parent consoles (drink holders)?',
        a: (
          <P>
            Yes, all strollers are equipped with a parent console for you to use
            during your rental.
          </P>
        ),
      },
      {
        q: 'Do you offer car seat adapters?',
        a: (
          <P>
            Due to insurance liability coverage, we do not offer car seat
            adapters and cannot allow their use on our rentals.
          </P>
        ),
      },
      {
        q: 'Do you offer the child tray on any of your strollers?',
        a: (
          <P>
            We offer the child tray for the City Mini and Mini GT single models
            for $10. You can add these to your order on the reservation page.
          </P>
        ),
      },
      {
        q: 'Do you offer rain covers?',
        a: (
          <>
            <P sx={{ mb: 1 }}>
              We have rain covers available for free ($50 replacement fee if not
              returned). If you&apos;d like a rain cover, please select it on
              the reservation page when placing your order. Rain covers are
              provided free of charge* but must be returned at the end of the
              rental.
            </P>
            <P sx={{ typography: 'caption', color: 'staticPages.text.light' }}>
              *Rain covers are free of charge when requested with your original
              order. Additional deliveries for accessories requested once
              initial delivery has been made may incur a $5 delivery fee at
              Kingdom Strollers&apos; discretion.
            </P>
          </>
        ),
      },
      {
        q: 'Do you offer cooler bags for the strollers?',
        a: (
          <>
            <P sx={{ mb: 1 }}>
              We offer free cooler bag attachments for the strollers ($10
              replacement fee if not returned). If you&apos;d like a cooler bag,
              please select it on the reservation page when placing your order.
              Cooler bags are provided free of charge* but must be returned at
              the end of the rental. Our cooler bags, while insulated, are not
              waterproof — for maximum effect, the use of an ice pack is
              recommended.
            </P>
            <P sx={{ typography: 'caption', color: 'staticPages.text.light' }}>
              *Cooler bags are free of charge when requested with your original
              order. Additional deliveries for accessories requested once
              initial delivery has been made may incur a $5 delivery fee at
              Kingdom Strollers&apos; discretion.
            </P>
          </>
        ),
      },
    ],
  },
  {
    label: 'Reservations, Changes & Payment',
    items: [
      {
        q: 'Can I order more than one item at a time?',
        a: (
          <P>
            Yes, you&apos;ll be able to order multiple items at once when
            placing your order.
          </P>
        ),
      },
      {
        q: 'What is your cancellation policy?',
        a: (
          <P>
            A full refund is issued for all cancellations made 48 hours or more
            prior to your rental date. All cancellations made within the 48-hour
            window will be charged the minimum rental fee of $40 for a single
            stroller and $50 for a double stroller. Please contact us as soon as
            possible should you need to cancel your stroller rental. Refunds for
            reservations made more than 120 days before the date of the
            cancellation request will require additional credit card information
            to process a credit.
          </P>
        ),
      },
      {
        q: 'What should I do if I need to extend my rental?',
        a: (
          <P>
            If you&apos;d like to extend the length of your rental, simply call
            us to let us know your new check-out date. We&apos;ll make every
            effort to accommodate your request subject to availability. (Rentals
            will be charged $20 beyond the original rental agreement, as this
            changes the rental rate.)
          </P>
        ),
      },
      {
        q: 'What happens if our plans change?',
        a: (
          <P>
            Please contact us via email to notify us of any changes regarding
            your stroller rental (check-in/out dates, resort location, etc.) and
            we&apos;ll do our best to accommodate your request.
          </P>
        ),
      },
      {
        q: 'Who handles your credit card transactions, and is it safe to order?',
        a: (
          <P>
            We use PayPal as our credit card processor, a leader in the merchant
            services field. Your credit card number will not be visible to us
            (unless the order is placed over the phone), and all information is
            securely encrypted when being transmitted.
          </P>
        ),
      },
      {
        q: 'When will I be charged?',
        a: (
          <P>
            Your credit card will be charged at the time your order is placed,
            and a confirmation email will be sent to you immediately.
          </P>
        ),
      },
    ],
  },
  {
    label: 'Lost, Damaged & Insurance',
    items: [
      {
        q: 'What if I have a problem or issue with the rental?',
        a: (
          <P>
            Our telephone number is listed on the rental. Give us a call and
            we&apos;ll make every effort to resolve any issue immediately.
          </P>
        ),
      },
      {
        q: 'Do you offer insurance on your rentals?',
        a: (
          <P>
            Insurance is available for a one-time charge per rental item,
            ranging between $10 and $25 depending on the item. The insurance
            waiver covers theft and accidental damage. It&apos;s optional, but
            recommended. You&apos;ll have the opportunity to purchase insurance
            on the &quot;Date and Resort&quot; page during the reservation
            process.
          </P>
        ),
      },
      {
        q: 'What happens if my child gets sick or has an accident in the stroller?',
        a: (
          <P>
            Accidents happen, and we&apos;re more than happy to replace your
            stroller if your child has an accident or gets sick in the stroller
            or crib. Due to the extra cleaning procedures needed to return the
            item to normal use, a $10 fee will be charged to exchange an item.
          </P>
        ),
      },
      {
        q: 'What happens if my stroller is lost or stolen?',
        a: (
          <P>
            If you can&apos;t find your stroller, contact any park employee who
            may be able to assist you — most strollers are moved around to
            create an organized space in parking areas. If you can&apos;t find
            it with the help of a park employee, contact guest services; they
            may direct you to lost and found. If guest services cannot locate
            it, fill out a lost item report and contact us immediately. If you
            believe your stroller was stolen, contact park security. You&apos;ll
            be responsible for the cost of replacement for any lost or stolen
            stroller unless you&apos;ve purchased stroller insurance. Stroller
            insurance is a cost-effective way to bring peace of mind to your
            vacation.
          </P>
        ),
      },
      {
        q: 'I left something behind in my rental — what can I do? (Lost & Found)',
        a: (
          <>
            <P>
              Forgot your AirTag, autograph books, or favorite Mickey Ears?
              Please email us with a description of the item and we&apos;ll do
              our best to find it. <B>Please note:</B> a flat $15 return
              shipping fee plus postage is charged for returned items.{' '}
              <span style={{ textDecoration: 'underline' }}>
                Canadian shipping rates are additional.
              </span>
            </P>
            <P>
              If we find an item with a value over $25, we&apos;ll usually try
              to reach out if we know who it belongs to. Return shipping charges
              are billed to the guest&apos;s original payment method.
            </P>
          </>
        ),
      },
      {
        q: 'What can I do to lessen the chances of it getting lost or stolen?',
        a: (
          <P>
            Always park the stroller in an approved parking area. Lock the brake
            when not in use. Make sure the tag on the bottom is not obscured and
            is clearly visible. Do not leave valuables in the stroller when
            you&apos;re not with it (purses, wallets, etc.). Please do not
            remove a wheel from a stroller — this puts undue stress on the brake
            assembly and may cause cracking or breakage.
          </P>
        ),
      },
    ],
  },
  {
    label: 'More Help',
    items: [
      {
        q: 'Tell me about the grocery coupon.',
        a: (
          <P>
            We&apos;ve partnered with Orlando Grocery Express to offer our
            guests a coupon for free grocery delivery when staying at a
            Disney-owned resort. When you place your reservation with Kingdom
            Strollers, your confirmation email will contain a coupon code that
            can be used on the Orlando Grocery Express website. Simply use the
            provided code when placing your grocery order for free grocery
            delivery!
          </P>
        ),
      },
      {
        q: 'Do you work with travel agents?',
        a: (
          <P>
            We work with some of the best travel agents in the country. If
            you&apos;re a travel agent, please email us and we can send you
            information on how to offer our service to your clients. We offer a
            great commissionable referral program available to all travel
            agents.
          </P>
        ),
      },
      {
        q: 'Other questions?',
        a: (
          <P>
            Have a question we didn&apos;t answer? Give us a call at{' '}
            <B navy>{PHONE_NUMBER}</B> or email us at{' '}
            <TextLink href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </TextLink>{' '}
            and we&apos;ll be happy to answer all of your questions. Let us help
            you make this your best vacation ever!
          </P>
        ),
      },
    ],
  },
];
